<script setup lang="ts">
  import { computed, ref } from 'vue'
  import {
    cn,
    FlagIcon,
    Modal,
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupContent,
    SidebarHeader,
    SidebarInset,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarProvider
  } from '@gat-ship/vue-components'
  import {
    IconClose,
    IconMaximize,
    IconMinimize,
    IconPort,
    IconTerminal,
    IconVessel
  } from '@gat-ship/icons'
  import { usePortDetailContext } from '../../composables/usePortDetail'
  import {
    getPortDetailSidebarItems,
    type PortDetailSection
  } from '../detail-modal/port-detail-modal-sections'
  import PortSectionPlaceholder from '../detail-modal/PortSectionPlaceholder.vue'
  import PortDetailsView from '../../views/PortDetailsView.vue'

  const open = defineModel<boolean>('open', { required: true })
  const activeSection = defineModel<PortDetailSection>('section', {
    required: true
  })

  const portDetail = usePortDetailContext()
  const port = computed(() => portDetail?.port.value)

  const sidebarItems = getPortDetailSidebarItems()

  const headerTitle = computed(() => port.value?.name ?? 'Port')

  // Viewport-filling mode, toggled from the header next to the close button.
  // Kept across open/close within the session (the modal root stays mounted),
  // but not persisted. Mirrors AgencyDetailModal.
  const isFullScreen = ref(false)

  // Overrides on top of sidebarMenuButtonVariants to match the other detail
  // modals (auto height, muted inactive text). ring-inset: the sidebar content
  // scrolls, which would clip a ring drawn outside the button bounds.
  const menuButtonClass =
    'text-muted-foreground hover:bg-accent/50 hover:text-foreground data-[active=true]:hover:bg-accent data-[active=true]:hover:text-accent-foreground h-auto cursor-pointer px-3 py-2 focus-visible:ring-inset'

  /** Mobile tab-bar item classes. */
  function tabClass(value: PortDetailSection) {
    return cn(
      'flex cursor-pointer items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm transition-colors',
      activeSection.value === value
        ? 'border-primary text-foreground font-medium'
        : 'text-muted-foreground hover:text-foreground hover:border-border border-transparent'
    )
  }
</script>

<template>
  <Modal
    v-model:open="open"
    type="dialog"
    :max-width="1000"
    full-screen-mobile
    hide-close-button
    :full-screen="isFullScreen"
  >
    <SidebarProvider
      :class="
        cn(
          'h-full min-h-0 w-full overflow-hidden',
          isFullScreen ? 'md:h-full' : 'md:h-[min(800px,85vh)] md:rounded-lg'
        )
      "
    >
      <ResizablePanelGroup
        direction="horizontal"
        auto-save-id="port-modal-sidebar"
      >
        <ResizablePanel
          class="hidden md:block"
          :default-size="22"
          :min-size="18"
          :max-size="40"
        >
          <Sidebar
            collapsible="none"
            class="bg-muted/50 hidden h-full w-full p-3 dark:bg-neutral-800/50 md:flex"
          >
            <SidebarHeader class="px-1 py-1">
              <div class="flex items-center gap-2">
                <FlagIcon
                  v-if="port"
                  :flag="port.locationCode.slice(0, 2)"
                  class="size-5 shrink-0 rounded-sm"
                />
                <div class="min-w-0">
                  <div class="text-foreground truncate text-sm font-medium">
                    {{ headerTitle }}
                  </div>
                  <div
                    v-if="port"
                    class="text-muted-foreground truncate text-xs"
                  >
                    {{ port.locationCode }}
                  </div>
                </div>
              </div>
            </SidebarHeader>
            <SidebarContent class="gap-1">
              <SidebarGroup class="p-0">
                <SidebarGroupContent>
                  <SidebarMenu>
                    <SidebarMenuItem
                      v-for="item in sidebarItems"
                      :key="item.key"
                    >
                      <SidebarMenuButton
                        :is-active="activeSection === item.key"
                        :class="menuButtonClass"
                        @click="activeSection = item.key"
                      >
                        <component :is="item.icon" />
                        <span>{{ item.label }}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </SidebarContent>
          </Sidebar>
        </ResizablePanel>
        <ResizableHandle class="hidden md:flex" />
        <ResizablePanel class="flex flex-col" :default-size="78">
          <SidebarInset class="overflow-hidden">
            <div class="relative z-10 shrink-0">
              <div class="bg-background flex items-center px-3 py-1.5">
                <div class="min-w-0 flex-1">
                  <span
                    class="text-foreground truncate pl-1 text-sm font-semibold md:hidden"
                  >
                    {{ headerTitle }}
                  </span>
                </div>
                <button
                  type="button"
                  class="text-muted-foreground hover:text-foreground hover:bg-accent hidden shrink-0 cursor-pointer rounded-md p-1.5 transition-colors md:block"
                  @click="isFullScreen = !isFullScreen"
                >
                  <IconMinimize v-if="isFullScreen" class="size-4" />
                  <IconMaximize v-else class="size-4" />
                  <span class="sr-only">
                    {{ isFullScreen ? 'Exit full screen' : 'Full screen' }}
                  </span>
                </button>
                <button
                  type="button"
                  class="text-muted-foreground hover:text-foreground hover:bg-accent shrink-0 cursor-pointer rounded-md p-1.5 transition-colors"
                  @click="open = false"
                >
                  <IconClose class="size-4" />
                  <span class="sr-only">Close</span>
                </button>
              </div>
              <nav
                class="border-border bg-background flex shrink-0 gap-1 overflow-x-auto border-b px-3 md:hidden"
              >
                <button
                  v-for="item in sidebarItems"
                  :key="item.key"
                  type="button"
                  :class="tabClass(item.key)"
                  @click="activeSection = item.key"
                >
                  <component :is="item.icon" class="size-4" />
                  <span>{{ item.label }}</span>
                </button>
              </nav>
              <div
                class="bg-background pointer-events-none h-3"
                style="
                  mask-image: linear-gradient(to bottom, black, transparent);
                "
              />
            </div>
            <div class="-mt-3 flex flex-1 flex-col gap-6 overflow-y-auto p-5">
              <PortDetailsView v-if="activeSection === 'details'" />
              <PortSectionPlaceholder
                v-else-if="activeSection === 'terminals'"
                :icon="IconTerminal"
                title="Terminals"
                description="Terminal management for this port is coming soon."
              />
              <PortSectionPlaceholder
                v-else-if="activeSection === 'berths'"
                :icon="IconVessel"
                title="Berths"
                description="Berth management for this port is coming soon."
              />
              <PortSectionPlaceholder
                v-else-if="activeSection === 'anchorages'"
                :icon="IconPort"
                title="Anchorages"
                description="Anchorage management for this port is coming soon."
              />
            </div>
          </SidebarInset>
        </ResizablePanel>
      </ResizablePanelGroup>
    </SidebarProvider>
  </Modal>
</template>
